import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { css } from 'aphrodite';

import styles from './styles';
import { ClickAwayListener, Popper } from '@mui/material';

// Global registry to track all dropdown close functions
const dropdownInstances = new Set();

const Dropdown = ({
  renderTrigger,
  children,
  width,
  horizontalPosition,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef();
  const instanceId = useRef(Math.random().toString(36)).current;

  const hideDropdown = useCallback(() => {
    setOpen(false);
  }, []);

  const showDropdown = useCallback(() => {
    // Close all other dropdowns
    dropdownInstances.forEach((instance) => {
      if (instance.id !== instanceId) {
        instance.close();
      }
    });

    // Open this dropdown
    setOpen(true);
  }, [instanceId]);

  // Store setOpen in a ref to avoid stale closures
  const setOpenRef = useRef(setOpen);
  setOpenRef.current = setOpen;

  // Register this dropdown instance when it mounts
  useEffect(() => {
    const instance = {
      id: instanceId,
      close: () => setOpenRef.current(false),
    };

    dropdownInstances.add(instance);

    return () => {
      dropdownInstances.delete(instance);
    };
  }, [instanceId]);

  // Close dropdown on scroll
  useEffect(() => {
    if (open) {
      const handleScroll = () => {
        hideDropdown();
      };

      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [open, hideDropdown]);

  const toggleDropdown = useCallback(() => {
    if (open) {
      hideDropdown();
    } else {
      showDropdown();
    }
  }, [open, hideDropdown, showDropdown]);

  const renderTriggerProps = {
    showDropdown,
    hideDropdown,
    toggleDropdown,
    open,
  };

  // Determine placement based on horizontalPosition
  const placement =
    horizontalPosition === 'left' ? 'bottom-start' : 'bottom-end';

  return (
    <>
      <ClickAwayListener onClickAway={hideDropdown}>
        <div
          style={{ position: 'relative' }}
          className={className}
          ref={anchorRef}
        >
          {renderTrigger(renderTriggerProps)}
        </div>
      </ClickAwayListener>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement={placement}
        style={{ zIndex: 9999 }}
        modifiers={[
          {
            name: 'offset',
            enabled: true,
            options: {
              offset: [0, 8],
            },
          },
          {
            name: 'flip',
            enabled: true,
            options: {
              fallbackPlacements: [
                'top-end',
                'top-start',
                'bottom-end',
                'bottom-start',
              ],
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              boundary: 'viewport',
              padding: 8,
              altAxis: true,
            },
          },
        ]}
      >
        <div
          data-testid="row-actions-dropdown"
          style={{ width: width || 'auto' }}
          className={css(styles.dropdown)}
        >
          {children}
        </div>
      </Popper>
    </>
  );
};

Dropdown.propTypes = {
  renderTrigger: PropTypes.func,
  horizontalPosition: PropTypes.string,
  children: PropTypes.node,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
};

Dropdown.defaultProps = {
  renderTrigger: () => {},
  horizontalPosition: 'right',
};

export default Dropdown;
